import React, { useMemo, useState } from 'react';
import { Button, Card, Empty, Form, Popconfirm, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { REGION_FILTERS_OPTIONS } from '@/utils/constant/appConstant';
import { useProductList } from '@/features/product-configurator/hooks/productSetup';
import { useAllConfigOptions } from '@/features/product-configurator/hooks/configOption';
import { useGetCurrentUser } from '@/hooks/app';
import { useBufferDaysRules, useCreateBufferDaysRule, useDeleteBufferDaysRule, useUpdateBufferDaysRule } from '../hooks';
import { BufferDaysRuleDrawer, IBufferDaysRuleFormValues } from './BufferDaysRuleDrawer';
import { IBufferDaysRule } from '../types';

const { Text } = Typography;

const tagWrapStyle: React.CSSProperties = { whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: '100%' };

const renderTags = (labels: string[]) => {
  if (labels.length === 0) return <Text type="secondary">Any</Text>;
  return (
    <div className="flex flex-wrap gap-1">
      {labels.map((label) => (
        <Tag key={label} style={tagWrapStyle}>{label}</Tag>
      ))}
    </div>
  );
};

const renderTag = (label?: string) => (label ? <Tag style={tagWrapStyle}>{label}</Tag> : <Text type="secondary">Any</Text>);

export const BufferDaysTab: React.FC = () => {
  const { data: allRules = [], isLoading: isRulesLoading } = useBufferDaysRules();
  const rules = useMemo(() => allRules.filter((r) => !r.deleted), [allRules]);
  const createMutation = useCreateBufferDaysRule();
  const updateMutation = useUpdateBufferDaysRule();
  const deleteMutation = useDeleteBufferDaysRule();

  const { data: currentUserData } = useGetCurrentUser();
  const currentUserEmail =
    currentUserData?.currentUser?.internalemailaddress || currentUserData?.currentUser?.domainname || 'unknown';

  const { data: productsData } = useProductList({ queryParams: { current: 1, pageSize: 200, filter: '' } });
  const { data: allOptionsData } = useAllConfigOptions();

  const [form] = Form.useForm<IBufferDaysRuleFormValues>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<IBufferDaysRule | null>(null);

  const productNameById = useMemo(
    () => new Map((productsData?.value ?? []).map((p: any) => [p.productid, p.name])),
    [productsData],
  );
  const optionNameById = useMemo(
    () => new Map((allOptionsData ?? []).map((o: any) => [o.dogma_configurationoptionsid, o.dogma_name])),
    [allOptionsData],
  );
  const regionLabelByValue = useMemo(
    () => new Map(REGION_FILTERS_OPTIONS.map((r) => [r.value, r.label])),
    [],
  );

  const openCreate = () => {
    setEditingRule(null);
    setDrawerOpen(true);
  };

  const openEdit = (rule: IBufferDaysRule) => {
    setEditingRule(rule);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingRule(null);
  };

  const handleFinish = (values: IBufferDaysRuleFormValues) => {
    const payload = {
      bufferDays: values.bufferDays,
      regions: values.regions ?? [],
      productIds: values.productIds ?? [],
      profileColourTypeId: values.profileColourTypeId,
      colourCombinations: values.colourCombinations ?? [],
      sprayId: values.sprayId,
      timberLockId: values.timberLockId,
      securityIds: values.securityIds ?? [],
      updatedBy: currentUserEmail,
    };

    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, ...payload }, { onSuccess: closeDrawer });
      return;
    }

    createMutation.mutate(payload, { onSuccess: closeDrawer });
  };

  return (
    <Card
      title="Buffer Days Configuration"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add Configuration
        </Button>
      }
    >
      <Text type="secondary" className="text-xs block mb-4">
        Override the default buffer days for a specific product, region, colour, spray or timber-look-joints
        configuration. Rules with fewer selections match more broadly — leave a field empty to match "any".
      </Text>

      <Table
        dataSource={rules}
        rowKey="id"
        size="small"
        pagination={false}
        loading={isRulesLoading}
        locale={{ emptyText: <Empty description="No buffer days configurations yet" /> }}
        scroll={{ x: 1650 }}
        columns={[
          {
            title: 'Buffer Days',
            key: 'bufferDays',
            width: 130,
            fixed: 'left',
            render: (_: unknown, record: IBufferDaysRule) => (
              <Tag color="blue">{record.bufferDays} working days</Tag>
            ),
          },
          {
            title: 'Region',
            key: 'regions',
            width: 180,
            render: (_: unknown, record: IBufferDaysRule) =>
              renderTags(record.regions.map((v) => regionLabelByValue.get(v) ?? String(v))),
          },
          {
            title: 'Product',
            key: 'productIds',
            width: 200,
            render: (_: unknown, record: IBufferDaysRule) =>
              renderTags(record.productIds.map((id) => productNameById.get(id) ?? id)),
          },
          {
            title: 'Profile Colour Type',
            key: 'profileColourTypeId',
            width: 220,
            render: (_: unknown, record: IBufferDaysRule) =>
              renderTag(record.profileColourTypeId ? optionNameById.get(record.profileColourTypeId) ?? record.profileColourTypeId : undefined),
          },
          {
            title: 'Internal / External Profile Colour',
            key: 'colourCombinations',
            width: 260,
            render: (_: unknown, record: IBufferDaysRule) => {
              if (record.colourCombinations.length === 0) return <Text type="secondary">Any</Text>;
              return (
                <div className="flex flex-col gap-1">
                  {record.colourCombinations.map((combo, index) => {
                    const internalLabel = combo.internalColorId
                      ? optionNameById.get(combo.internalColorId) ?? combo.internalColorId
                      : 'Any';
                    const externalLabel = combo.externalColorId
                      ? optionNameById.get(combo.externalColorId) ?? combo.externalColorId
                      : 'Any';
                    return (
                      <Tag key={index} style={tagWrapStyle}>
                        {internalLabel} / {externalLabel}
                      </Tag>
                    );
                  })}
                </div>
              );
            },
          },
          {
            title: 'Spray',
            key: 'sprayId',
            width: 120,
            render: (_: unknown, record: IBufferDaysRule) =>
              renderTag(record.sprayId ? optionNameById.get(record.sprayId) ?? record.sprayId : undefined),
          },
          {
            title: 'Timber Look Joints',
            key: 'timberLockId',
            width: 180,
            render: (_: unknown, record: IBufferDaysRule) =>
              renderTag(record.timberLockId ? optionNameById.get(record.timberLockId) ?? record.timberLockId : undefined),
          },
          {
            title: 'Security',
            key: 'securityIds',
            width: 180,
            render: (_: unknown, record: IBufferDaysRule) =>
              renderTags(record.securityIds.map((id) => optionNameById.get(id) ?? id)),
          },
          {
            title: '',
            key: 'actions',
            width: 90,
            fixed: 'right',
            render: (_: unknown, record: IBufferDaysRule) => (
              <div className="flex gap-1">
                <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
                <Popconfirm
                  title="Delete this configuration? This cannot be undone."
                  onConfirm={() => deleteMutation.mutate({ id: record.id, updatedBy: currentUserEmail })}
                  okText="Delete"
                  okButtonProps={{ danger: true, loading: deleteMutation.isPending }}
                >
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            ),
          },
        ]}
      />

      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <BufferDaysRuleDrawer
          open={drawerOpen}
          editingRule={editingRule}
          onClose={closeDrawer}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      </Form>
    </Card>
  );
};

export default BufferDaysTab;
